import React from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import {
  getPlatformIconPath,
  getPlatformDisplayName,
} from "../../utils/iconUtils";
import { useSelector } from "react-redux";
import { selectUserDb } from "../../store/slices/session/sessionSelector";

export interface Campaign {
  id?: string;
  name: string;
  date: string;
  platform: string;
}

interface CampaignCardProps {
  title: string;
  subtitle: string;
  campaigns: Campaign[];
}

const CampaignCard: React.FC<CampaignCardProps> = ({
  title,
  subtitle,
  campaigns,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userDb = useSelector(selectUserDb);

  const isAdmin = userDb?.role === "ADMIN";

  const handleCreateCampaign = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate("/campaigns/new");
  };

  const handleCardClick = () => {
    navigate("/campaigns");
  };

  const handleCampaignClick = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation();
    if (campaign.id) {
      navigate(`/campaigns/${campaign.id}`);
    }
  };

  return (
    <Paper
      onClick={handleCardClick}
      sx={{
        p: 2,
        borderRadius: 2,
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.04)",
        border: `1px solid ${theme.palette.neutral[200]}`,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        gap: 0,
        cursor: "pointer",
        "&:hover": {
          borderColor: theme.palette.primary.main,
        },
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography variant="h6" fontWeight={700} color="primary">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        </Box>

        {/* Add Campaign Button */}
        {isAdmin && (
          <IconButton
            onClick={handleCreateCampaign}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: "50%",
              width: 32,
              height: 32,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
              cursor: "pointer",
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Campaign List */}
      {campaigns.map((campaign, index) => (
        <Box
          key={index}
          onClick={(e) => handleCampaignClick(e, campaign)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1,
            borderRadius: 1,
            overflow: "hidden", // Prevent overflow
            width: "100%", // Ensure full width
            cursor: campaign.id ? "pointer" : "default",
            "&:hover": campaign.id
              ? { backgroundColor: theme.palette.neutral[100] }
              : undefined,
          }}
        >
          {/* Left: Platform Icon & Campaign Info */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              minWidth: 0, // Important for flex child to allow shrinking below min-content
              flex: 1, // Take available space
              overflow: "hidden", // Contain children
            }}
          >
            <Box
              sx={{
                width: 20,
                height: 20,
                flexShrink: 0, // Prevent icon from shrinking
              }}
            >
              <Box sx={{ width: 20, height: 20 }}>
                <img
                  src={getPlatformIconPath(campaign.platform)}
                  alt={getPlatformDisplayName(campaign.platform)}
                  width="100%"
                  height="100%"
                />
              </Box>
            </Box>
            <Box
              sx={{
                minWidth: 0, // Allow shrinking below min-content
                overflow: "hidden", // Contain text overflow
                flex: 1, // Take available space
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                color="primary"
                noWrap
                title={campaign.name} // Show full name on hover
                sx={{ textOverflow: "ellipsis", lineHeight: 1.2 }}
              >
                {campaign.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ lineHeight: 1.2, display: "block" }}
              >
                {campaign.date}
              </Typography>
            </Box>
          </Box>

          {/* Platform Badge */}
          <Chip
            label={getPlatformDisplayName(campaign.platform)}
            size="small"
            sx={{
              fontSize: "10px",
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              borderRadius: "50px",
              px: 1,
              ml: 1, // Add margin to separate from text
              flexShrink: 0, // Prevent badge from shrinking
              maxWidth: { xs: "80px", sm: "100px" }, // Limit width on small screens
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          />
        </Box>
      ))}
    </Paper>
  );
};

export default CampaignCard;
